import { Modal, Spin, Table } from "antd";
import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { Locale } from "../../../utils";
import {
  handleMutationError,
  showNotificationWithIcon,
} from "../../../utils/helpers/helpers";
import {
  getList,
  remove,
  create,
  update,
} from "../../../utils/http/dataProvider";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import { BreadCrumb } from "../../../components/main/LayoutComponents";

import "./CategoryList.scss";
import { categoryListColumns } from "./categoryListColumns";
import CategoryListHeader from "./CategoryListHeader";
import AddOrEditCategory from "./AddOrEditCategory";
const i18n = Locale.getInstance();

const CategoryList = () => {
  const user = useSelector((state) => getUserInfo(state));
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState();
  const [perPage, setPerPage] = useState(6);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const categoryFormRef = useRef();

  const selectCategory = (item) => {
    setCategory(item);
    const values = {
      parent_course_category_id: item?.parent_course_category_id,
      zh_name: item?.zh_name,
      ug_name: item?.ug_name,
      ug_image: item?.ug_image_media ? [item.ug_image_media] : [],
      zh_image: item?.zh_image_media ? [item.zh_image_media] : [],
      order: item?.order,
      status: !!item?.status,
      recommend: item?.recommend === 1,
    };

    setTimeout(() => {
      categoryFormRef.current?.setFieldsValue(values);
    }, 100);

    setOpenEditModal(true);
  };
 
  const handleSave = () => {
    const values = categoryFormRef.current.getFieldsValue();

    if (values.zh_image?.length > 0) {
      values.zh_image = values.zh_image[0].file_path;
    }
    if (values.ug_image?.length > 0) {
      values.ug_image = values.ug_image[0].file_path;
    }

    setLoading(true);
    const params = {
      data: values,
      token: user.token,
    };

    if (category) {
      updateCategoryMutation.mutate({
        resource: "course/categories",
        params: { ...params, id: category.id },
      });
    } else {
      addCategoryMutation.mutate({ resource: "course/categories", params });
    }
  };

  const addCategoryMutation = useMutation(create, {
    onSuccess: () => {
      queryClient.invalidateQueries("course-categories");
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      setLoading(false);
      setOpenEditModal(false);
      categoryFormRef.current?.resetFields();
    },
    onError: (error) => {
      handleMutationError(error);
      setOpenEditModal(false);
      setLoading(false);
    },
  });

  const updateCategoryMutation = useMutation(update, {
    onSuccess: () => {
      queryClient.invalidateQueries("course-categories");
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      setOpenEditModal(false);
      setLoading(false);
      categoryFormRef.current?.resetFields();
    },
    onError: (error) => {
      handleMutationError(error);
      setOpenEditModal(false);
      setLoading(false);
    },
  });

  const { data, error, isLoading } = useQuery(
    ["course-categories", user.token, page, perPage],
    () =>
      getList("course/categories", {
        pagination: {
          perPage,
          page,
        },
        sort: {
          field: "id",
          order: "desc",
        },

        token: user.token,
      })
  );
  if (error) {
    showNotificationWithIcon(error.data.message);
  }
  const breadCrumbData = [
    {
      href: "/main/courses",
      title: i18n.t("course.title"),
    },
    {
      href: "/main/course/categories",
      title: i18n.t("course.category.title"),
    },
  ];

  const deleteMutation = useMutation(remove, {
    onSuccess: () => {
      queryClient.invalidateQueries("course-categories");
      setLoading(false);
    },
    onError: (error) => {
      setLoading(false);
      handleMutationError(error);
    },
  });
  if (isLoading) {
    return <Spin spinning={isLoading} />;
  }
  return (
    <>
      <BreadCrumb data={breadCrumbData} />
      <div className="category">
        <CategoryListHeader selectCategory={selectCategory} />
        <Table
          childrenColumnName="sub_categories"
          expandable={{ defaultExpandAllRows: true }}
          className="list"
          rowKey="id"
          columns={categoryListColumns(
            deleteMutation,
            user.token,
            setLoading,
            selectCategory
          )}
          dataSource={data?.data}
          scroll={{
            y: 600,
          }}
          pagination={{
            current: page,
            total: data?.total,
            pageSize: perPage,
            onChange: (pagination) => {
              setPage(pagination);
            },
          }}
        />
      </div>
      <Modal
        title={
          category
            ? i18n.t("course.category.edit")
            : i18n.t("course.category.add")
        }
        open={openEditModal}
        onOk={handleSave}
        onCancel={() => {
          setOpenEditModal(false);
          categoryFormRef.current?.resetFields();
        }}
        okText={i18n.t("base.modal.okText")}
        cancelText={i18n.t("base.modal.cancelText")}
        width={880}
      >
        <AddOrEditCategory
          ref={categoryFormRef}
          loading={loading}
          category={category}
        />
      </Modal>
    </>
  );
};

export default CategoryList;
